/**
 * Weapon registry — one file per weapon, discovered by glob so fifty authors
 * can each own `defs/<id>.js` without ever touching this file.
 *
 * A weapon module exports:
 *   WEAPON      the tuning data (see CONTRACT.md for every field)
 *   loadModel   () => Promise<buildFn> — a LAZY dynamic import of its model
 *               builder in `../models/<id>.js`. Never import a model builder
 *               eagerly here: fifty weapons' worth of geometry built at boot
 *               is a multi-second stall (see WeaponSystem.init()), so only the
 *               player's equipped loadout ever calls loadModel().
 */
const modules = import.meta.glob('./*.js', { eager: true });

export const WEAPON_DEFS = {};
export const MODEL_LOADERS = {};

for (const path in modules) {
  if (path.endsWith('/index.js')) continue;
  const mod = modules[path];
  if (!mod.WEAPON) continue;
  WEAPON_DEFS[mod.WEAPON.id] = mod.WEAPON;
  MODEL_LOADERS[mod.WEAPON.id] = mod.loadModel;
}

export const WEAPON_IDS = Object.keys(WEAPON_DEFS);

/** The four fixed loadout lanes shown by the home-screen armory. */
export const LOADOUT_SLOTS = Object.freeze(['primary', 'secondary', 'melee', 'utility']);

/** A fresh profile has one usable item in every lane. `frag` is the existing
 * throwable rather than a viewmodel weapon, so it intentionally has no def. */
export const STARTER_LOADOUT = Object.freeze({
  primary: 'rifle',
  secondary: 'pistol',
  melee: 'fists',
  utility: 'frag',
});

/** Explicit `slot` wins for unusual weapons; class supplies the safe default. */
export function slotForWeapon(weapon) {
  const w = typeof weapon === 'string' ? WEAPON_DEFS[weapon] : weapon;
  if (!w) return null;
  if (LOADOUT_SLOTS.includes(w.slot)) return w.slot;
  if (w.class === 'melee') return 'melee';
  if (w.class === 'pistol') return 'secondary';
  if (w.class === 'launcher') return 'utility';
  return 'primary';
}
